import { c as _c } from "react/compiler-runtime";
import { TimeRangeConfig } from '@/api/generated/model';
import { TimeRangePicker } from '@/comps/date-picker-button';
import { FilterPickerV2, GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { LoadingSpinner } from '@/comps/loading-spinner';
import { Button } from '@/comps/primitives/button';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { useAuthContext } from '@/hooks/use-auth';
import { FilterIcon, Globe } from 'lucide-react';
import { useProjectsContext } from '@/providers/projects-provider';
import { CreateCustomProjectModal } from '@/domains/dashboard/components/CreateCustomProjectModal';
import { cn } from '@/utils/shad';
import { WithClassName } from '@/types/component';
import { BasicTooltip } from '@/comps/basic-tooltip';
import { useConversationDeeplink } from '@/hooks/useConversationDeeplink';
import { useInteractionDeeplink } from '@/hooks/useInteractionDeeplink';
import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { usePathname } from 'next/navigation';
import { getLiveActivitySectionFromPath, isLiveActivityPath } from '@/app/(authenticated)/(dashboard)/live-activity/constants';
import { getDeepLinkIdOnlyTooltipText } from '@/utils/deep-link.utils';
import { usePageScopedGlobalFilterTimeRange } from '@/hooks/use-page-scoped-global-filter-time-range';
import { isFilterDefined } from '@/models/common/filters';
import type { PopperContentProps } from '@radix-ui/react-popper';
export type AggregateBy = 'interaction' | 'conversation';
function useScopedDeepLinkState() {
  const $ = _c(7);
  const {
    deepLinkConversationId
  } = useConversationDeeplink();
  const {
    deepLinkInteractionId
  } = useInteractionDeeplink();
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = getLiveActivitySectionFromPath(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const liveActivitySection = t0;
  let t1;
  bb0: {
    if (deepLinkInteractionId) {
      t1 = "interaction" as const;
      break bb0;
    }
    if (deepLinkConversationId) {
      t1 = "conversation" as const;
      break bb0;
    }
    t1 = null;
  }
  const deepLinkType = t1;
  let t2;
  bb1: {
    if (!deepLinkType) {
      let t3;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = {
          deepLinkType: null,
          isDeepLink: false
        };
        $[2] = t3;
      } else {
        t3 = $[2];
      }
      t2 = t3;
      break bb1;
    }
    if (!isLiveActivityPath(pathname)) {
      let t3;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = {
          deepLinkType: null,
          isDeepLink: false
        };
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      t2 = t3;
      break bb1;
    }
    const isMatchingTab = deepLinkType === "interaction" && liveActivitySection === "interactions" || deepLinkType === "conversation" && liveActivitySection === "conversations";
    const t3 = isMatchingTab ? deepLinkType : null;
    let t4;
    if ($[4] !== isMatchingTab || $[5] !== t3) {
      t4 = {
        deepLinkType: t3,
        isDeepLink: isMatchingTab
      };
      $[4] = isMatchingTab;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t2 = t4;
  }
  return t2;
}
export const GlobalFiltersRow = t0 => {
  const $ = _c(14);
  const {
    className
  } = t0;
  const {
    setTimeRange,
    timeRange,
    timeRangeLoading
  } = useGlobalFiltersContext();
  const {
    isSharedLink
  } = useAuthContext();
  const {
    deepLinkType,
    isDeepLink
  } = useScopedDeepLinkState();
  let t1;
  bb0: {
    if (!deepLinkType) {
      t1 = "";
      break bb0;
    }
    let t2;
    if ($[0] !== deepLinkType) {
      t2 = getDeepLinkIdOnlyTooltipText(deepLinkType);
      $[0] = deepLinkType;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    t1 = t2;
  }
  const deepLinkTooltipText = t1;
  const t2 = `flex items-center border-b ${isSharedLink ? "justify-end" : "justify-between"}`;
  let t3;
  if ($[2] !== className || $[3] !== t2) {
    t3 = cn(t2, className);
    $[2] = className;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== deepLinkTooltipText || $[6] !== isDeepLink || $[7] !== setTimeRange || $[8] !== timeRange || $[9] !== timeRangeLoading) {
    t4 = <div className="ml-auto flex items-center gap-1">{timeRangeLoading ? <Button variant="outline" disabled={true}><LoadingSpinner size={16} /></Button> : <BasicTooltip show={isDeepLink} text={deepLinkTooltipText}><span><TimeRangePicker key={JSON.stringify(timeRange)} defaultValue={timeRange} onChange={setTimeRange} disabled={isDeepLink} /></span></BasicTooltip>}</div>;
    $[5] = deepLinkTooltipText;
    $[6] = isDeepLink;
    $[7] = setTimeRange;
    $[8] = timeRange;
    $[9] = timeRangeLoading;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
type GlobalFilterPickerProps = {
  projectId: string;
  /** When set (including `undefined`), overrides context `timeRange` for filter scoping. */
  timeRangeOverride?: TimeRangeConfig | undefined;
  renderTrigger?: (props: {
    disabled: boolean;
    appliedCount: number;
  }) => ReactElement;
  popoverSide?: PopperContentProps['side'];
};
export function GlobalFilterPicker(t0) {
  const $ = _c(42);
  const {
    projectId,
    timeRangeOverride,
    renderTrigger,
    popoverSide
  } = t0;
  const {
    filters,
    setFilters
  } = useGlobalFiltersContext();
  const pageScopedTimeRange = usePageScopedGlobalFilterTimeRange();
  const {
    isSharedLink,
    isCompanyAdmin,
    canWrite,
    isNebulyAdmin,
    hasPermission
  } = useAuthContext();
  const {
    organizationId,
    isMappedProject
  } = useProjectsContext();
  const {
    deepLinkType,
    isDeepLink
  } = useScopedDeepLinkState();
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [createProjectFilters, setCreateProjectFilters] = useState(undefined);
  const [pickerInstance, setPickerInstance] = useState(0);
  let t1;
  bb0: {
    if (!deepLinkType) {
      t1 = "";
      break bb0;
    }
    let t2;
    if ($[0] !== deepLinkType) {
      t2 = getDeepLinkIdOnlyTooltipText(deepLinkType);
      $[0] = deepLinkType;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    t1 = t2;
  }
  const deepLinkTooltipText = t1;
  const effectiveTimeRange = timeRangeOverride !== undefined ? timeRangeOverride : pageScopedTimeRange;
  const triggerDisabled = isSharedLink || isDeepLink;
  let t2;
  if ($[2] !== filters) {
    t2 = filters.filter(_temp);
    $[2] = filters;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const appliedCount = t2.length;
  let t3;
  if ($[4] !== canWrite || $[5] !== hasPermission || $[6] !== isCompanyAdmin || $[7] !== isNebulyAdmin) {
    t3 = hasPermission("read:*") && (isNebulyAdmin || isCompanyAdmin || canWrite);
    $[4] = canWrite;
    $[5] = hasPermission;
    $[6] = isCompanyAdmin;
    $[7] = isNebulyAdmin;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const canCreateProjectFromFilters = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = nextFilters => {
      setCreateProjectFilters(nextFilters);
      setCreateProjectModalOpen(true);
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleCreateProjectFromFilters = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setPickerInstance(_temp2);
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleProjectCreated = t5;
  let t6;
  bb1: {
    if (!canCreateProjectFromFilters) {
      t6 = undefined;
      break bb1;
    }
    const t7 = !isMappedProject;
    let t8;
    if ($[11] !== t7) {
      t8 = {
        onSaveForEveryone: handleCreateProjectFromFilters,
        canSaveForEveryone: t7,
        requireDefinedFilters: true,
        appliesDraftOnSave: false,
        saveLabel: "Create a new project",
        saveDisabledMessage: "You are already viewing a mapped project.",
        noDefinedFiltersMessage: "Add at least one global filter to create a project from it.",
        saveTooltip: "Create a new mapped project from the current global filters"
      };
      $[11] = t7;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    t6 = t8;
  }
  const globalDraftActions = t6;
  const t7 = renderTrigger ? FilterIcon : Globe;
  const t8 = !!globalDraftActions;
  let t9;
  if ($[13] !== appliedCount || $[14] !== renderTrigger || $[15] !== triggerDisabled) {
    t9 = renderTrigger?.({
      disabled: triggerDisabled,
      appliedCount
    });
    $[13] = appliedCount;
    $[14] = renderTrigger;
    $[15] = triggerDisabled;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== createProjectModalOpen || $[18] !== effectiveTimeRange || $[19] !== filters || $[20] !== globalDraftActions || $[21] !== pickerInstance || $[22] !== popoverSide || $[23] !== projectId || $[24] !== setFilters || $[25] !== t7 || $[26] !== t8 || $[27] !== t9 || $[28] !== triggerDisabled) {
    t10 = <span><FilterPickerV2 key={pickerInstance} title="Global filters" filters={filters} setFilters={setFilters} projectId={projectId} volumeKind="users" disabled={triggerDisabled} timeRange={effectiveTimeRange} align="right" side={popoverSide} type="user_intelligence" icon={t7} isGlobal={true} modal={true} showDraftActions={t8} draftActions={globalDraftActions} hasDraftEdits={false} keepOpen={createProjectModalOpen}>{t9}</FilterPickerV2></span>;
    $[17] = createProjectModalOpen;
    $[18] = effectiveTimeRange;
    $[19] = filters;
    $[20] = globalDraftActions;
    $[21] = pickerInstance;
    $[22] = popoverSide;
    $[23] = projectId;
    $[24] = setFilters;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = triggerDisabled;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== deepLinkTooltipText || $[31] !== isDeepLink || $[32] !== t10) {
    t11 = <BasicTooltip show={isDeepLink} text={deepLinkTooltipText}>{t10}</BasicTooltip>;
    $[30] = deepLinkTooltipText;
    $[31] = isDeepLink;
    $[32] = t10;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== canCreateProjectFromFilters || $[35] !== createProjectFilters || $[36] !== createProjectModalOpen || $[37] !== organizationId) {
    t12 = canCreateProjectFromFilters && <CreateCustomProjectModal key={organizationId} open={createProjectModalOpen} onOpenChange={setCreateProjectModalOpen} onConfirm={handleProjectCreated} filters={createProjectFilters} />;
    $[34] = canCreateProjectFromFilters;
    $[35] = createProjectFilters;
    $[36] = createProjectModalOpen;
    $[37] = organizationId;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t11 || $[40] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  return t13;
}
function _temp2(instance) {
  return instance + 1;
}
function _temp(filter) {
  return isFilterDefined(filter);
}